import { v4 as uuid } from 'uuid';
import { decisionTreeCaseNotHandled, loadLongTransaction, setLoading } from '../../index';
import {
  getLineItemsOfSelectedOrder,
  getOrderCreationDate,
  getOrderId,
  getOrderingSystem,
  getOrderModificationsAreAllowed,
  getOrderShipmentsOfSelectedOrder,
  getRefundAmount,
  getReturnedItems,
  getSelectedLineItems,
  getSelectedLineItemsByShipmentStatus,
  getSelectedOrder,
  getShippoInfoByShipmentId,
  getStatusOfItemsOfSelectedOrderByItemId,
} from '../../../selectors/tools';
import {
  extractSelectedTicketFromState,
  getChildrenOfCurrentDecisionNode,
  getDecisionNodeAtPath,
  getRecentDeliveryThreshold,
  getRecentOrderThreshold,
  getSimplrAccountIdOfSelectedTicket,
  getStuckInTransitThresholdOfSelectedTicket,
} from '../../../extractFromState';
import * as ordersActions from '../OrdersSearchActions';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import { addEdgeToDecisionTreePath, addSnippetText } from '../../searchActions';
import {
  CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES,
  DECISION_NODE_ACTIONS,
  ITEMS_COMPLETE,
  LINE_ITEM_ORDER_STATUS_TOKENS,
  PARTIAL_REFUND_DECISION_EDGES,
  RECENT_DELIVERY_THRESHOLD,
  RECENT_ORDER_THRESHOLD,
  REFUND_AMOUNT_TYPE,
  STORE_CREDIT_STATUS_TOKENS,
  STUCK_IN_TRANSIT_THRESHOLD,
} from '../../../constants/decisionTree';
import {
  captureException,
  checkIfTrackingInfoTooOldForCarrier,
  convertDollarsToCents,
  countItems,
  getCanceledItemsInOrder,
  getCarrierCodeOfShipment,
  getItemId,
  getItemName,
  getLastTrackingUpdateDate,
  getMappedValueFromOrderOrItem,
  getNumberOfTrackings,
  getOrderShipmentFromOrderShipmentsList,
  getTrackingStatus,
  hasShippingCharges,
  isCarrierSupportedInShippo,
  isDayLessThanTresholdAgo,
  isItemCompletelyRefunded,
  isOrderCanceled,
  isOrderPartiallyOrFullyRefunded,
  isOrderRefunded,
  isOrderShippingRefunded,
  itemIsUPSSurepost,
  MultipleTrackingNumberError,
  orderItemsWereCanceled,
  priceFormat,
  processForRefundCalculation,
  replaceTemplateTextForLineItems,
  UnexpectedDecisionTreeEdge,
  UnexpectedShippingStatus,
  UnhandledDecisionTreeCase,
} from '../../../util';
import { commonOrdersPostQuery } from '../../../api/orders/shared';
import { replaceTemplatedTextForMultipleItems } from '../../../selectors/templating';
import { resetPartialRefundStore, setPartialRefundAmount } from '../PartialRefundActions';
import { cancelRefund, fetchCalculateInfo } from '../CommonOrderActions';
import { shopifyGetOrderShippingRefundAmount } from '../ShopifyActions';
import { setRefundShippingAmount, setReturnedItems } from '../OrdersSearchActions';
import { ErrorStrings } from '../../../constants/fixedMessages';
import {
  trackDecisionTreeItemRefunded,
  trackDecisionTreeOrderStatusLineItemsSelected,
  trackDecisionTreeOrderStatusSelectionComplete,
} from '../../../analytics/searchEventAnalytics';
import AuthService from 'services/authService';

// USED FOR: order status
// Note: This function uses MACRO_SNIPPET, not SEND_MACRO in order to handle multiple line items
// All successfully processed orders will end on ITEMS_COMPLETE after iterating over
// MACRO_SNIPPET nodes and adding snippetText to the end macro
export const loadInfoForSelectedOrderItems = () => {
  return async (dispatch, getState) => {
    const state = getState();

    trackDecisionTreeOrderStatusLineItemsSelected();

    // clears previous snippets
    dispatch(ordersActions.clearSnippetText());
    try {
      let orderingSystem = getOrderingSystem(state);
      let orderShipments = getOrderShipmentsOfSelectedOrder(state);
      const decisionTreeChildren = getChildrenOfCurrentDecisionNode(state);
      const orderCreationDate = getOrderCreationDate(state);
      const stuckInTransitThreshold = getStuckInTransitThresholdOfSelectedTicket(state) || STUCK_IN_TRANSIT_THRESHOLD;
      const recentDeliveryThreshold = getRecentDeliveryThreshold(state) || RECENT_DELIVERY_THRESHOLD;
      const recentOrderThreshold = getRecentOrderThreshold(state) || RECENT_ORDER_THRESHOLD;
      const itemsByShipmentStatus = getSelectedLineItemsByShipmentStatus(state);
      const shippoInfoByShipmentId = getShippoInfoByShipmentId(state);
      const order = getSelectedOrder(state);

      // iterate through each shipment status and add each relevant snippet
      // eslint-disable-next-line no-restricted-syntax
      for (let shipmentStatus in itemsByShipmentStatus) {
        const shipmentData = JSON.parse(shipmentStatus);
        const { shipmentId, shipping_date } = shipmentData;
        const orderShipment = getOrderShipmentFromOrderShipmentsList(orderingSystem, orderShipments, shipmentId);
        const shippoInfo = shippoInfoByShipmentId[shipmentId];

        let foundAny = false;

        const currentPath = [];
        for (let i = 0; i < LINE_ITEM_ORDER_STATUS_TOKENS.length; i++) {
          const options = LINE_ITEM_ORDER_STATUS_TOKENS[i];
          const lastOptions = LINE_ITEM_ORDER_STATUS_TOKENS[i - 1];
          const lastChoice = currentPath[i - 1];
          // TODO: Add default case
          // eslint-disable-next-line default-case
          switch (i) {
            case 0:
              // first check if the order was refunded
              if (isOrderRefunded(order, orderingSystem)) {
                currentPath.push(options.ORDER_REFUNDED);
              } else {
                // if this shipment is fulfilled, we should have an id of the shipment
                currentPath.push(!!orderShipment ? options.ITEM_FULFILLED : options.ITEM_UNFULFILLED);
              }
              break;
            case 1:
              // see if fulfilled
              if (lastChoice === lastOptions.ITEM_FULFILLED) {
                if (getNumberOfTrackings(orderShipment, orderingSystem)) {
                  currentPath.push(options.HAS_TRACKING);
                } else {
                  currentPath.push(options.NO_TRACKING);
                }
              } else {
                currentPath.push(shipping_date ? options.HAS_SHIPPING_DATE : options.NO_SHIPPING_DATE);
              }
              break;
            case 2:
              if (lastChoice === lastOptions.HAS_TRACKING) {
                // check length of tracking numbers
                if (getNumberOfTrackings(orderShipment, orderingSystem) > 1) {
                  throw new MultipleTrackingNumberError();
                }
                const carrier_code = getCarrierCodeOfShipment(orderShipment, orderingSystem);
                const { created_at } = orderShipment;
                const carrierSupportedInShippo = isCarrierSupportedInShippo(orderShipment, orderingSystem);
                const trackingStatus = getTrackingStatus(
                  orderShipment,
                  shippoInfo,
                  orderingSystem,
                  carrierSupportedInShippo
                );

                if (!carrierSupportedInShippo) {
                  currentPath.push(options.UNSUPPORTED_CARRIER);
                  // check to see if tracking is too old
                } else if (checkIfTrackingInfoTooOldForCarrier(carrier_code, created_at)) {
                  currentPath.push(options.NO_LONGER_STORED);
                  // next check if we have a tracking status
                } else if (trackingStatus) {
                  // check if returned status matches our options
                  const shipmentOption = options[trackingStatus];
                  // if status doesn't map to our options, we can't handle this case
                  if (!shipmentOption) {
                    throw new UnexpectedShippingStatus(shipmentOption);
                  }
                  // otherwise add to our path
                  currentPath.push(shipmentOption);
                } else {
                  // no tracking status means we are in pre-transit
                  currentPath.push(options.PRE_TRANSIT);
                }
              } else if (lastChoice === lastOptions.NO_SHIPPING_DATE) {
                if (isDayLessThanTresholdAgo(orderCreationDate, recentOrderThreshold)) {
                  currentPath.push(options.RECENT_ORDER);
                } else {
                  currentPath.push(options.OLD_ORDER);
                }
              }
              break;
            case 3:
              if (lastChoice === lastOptions.TRANSIT) {
                // check the last time the tracking has been updated for shippo
                const lastShippingUpdateDate = getLastTrackingUpdateDate(orderShipment, shippoInfo, orderingSystem);
                if (isDayLessThanTresholdAgo(lastShippingUpdateDate, stuckInTransitThreshold)) {
                  currentPath.push(options.NORMAL);
                } else {
                  currentPath.push(options.STUCK);
                }
              } else if (lastChoice === lastOptions.DELIVERED) {
                const lastShippingUpdateDate = getLastTrackingUpdateDate(orderShipment, shippoInfo, orderingSystem);
                if (isDayLessThanTresholdAgo(lastShippingUpdateDate, recentDeliveryThreshold)) {
                  if (itemIsUPSSurepost(shippoInfo)) {
                    currentPath.push(options.UPS_SUREPOST);
                  } else {
                    currentPath.push(options.RECENT);
                  }
                } else {
                  currentPath.push(options.OLD);
                }
              }
              break;
          }

          const strPath = currentPath.join('__');
          if (!foundAny && strPath in decisionTreeChildren) {
            const decisionNode = decisionTreeChildren[strPath];
            // TODO: Add default case
            // eslint-disable-next-line default-case
            switch (decisionNode.action) {
              // if escalate, dispatch action and quit
              case DECISION_NODE_ACTIONS.ESCALATE_TO_PARTNER:
              case DECISION_NODE_ACTIONS.ESCALATE_TO_SPECIALISTS:
                return dispatch(addEdgeToDecisionTreePath(strPath));
              // add the snippets
              case DECISION_NODE_ACTIONS.MACRO_SNIPPET:
                const lineItems = itemsByShipmentStatus[shipmentStatus];
                const text = replaceTemplateTextForLineItems(
                  decisionNode.snippetText,
                  lineItems,
                  orderShipment,
                  orderingSystem
                );
                dispatch(addSnippetText(text));
                break;
            }
            foundAny = true;
          }
        }
        // if we didn't find any, then we need to escalate because we couldn't find a match
        if (!foundAny) {
          const strPath = currentPath.join('__');
          throw new UnexpectedDecisionTreeEdge(strPath);
        }
      }

      // once complete, go to edge
      trackDecisionTreeOrderStatusSelectionComplete();

      return dispatch(addEdgeToDecisionTreePath(ITEMS_COMPLETE));
    } catch (err) {
      if (err instanceof UnhandledDecisionTreeCase) {
        return dispatch(decisionTreeCaseNotHandled(err.message));
      }
      throw err;
    }
  };
};

export const loadCancellationsBasedOffSelectedOrder = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedTicket = extractSelectedTicketFromState(state);
    if (!selectedTicket) {
      return showErrorPopup(ErrorStrings.SELECTED_TICKET_NOT_FOUND, 'Error');
    }

    try {
      dispatch(setLoading(true, 'searchPage'));
      const orderSelected = getSelectedOrder(state);
      const selectedOrderWasCanceled = orderItemsWereCanceled(orderSelected);

      if (selectedOrderWasCanceled) {
        const canceledItemsInOrder = getCanceledItemsInOrder(orderSelected);
        dispatch(ordersActions.setCanceledItems(canceledItemsInOrder));
        // multiple items of same type are grouped as one item
        const numOfCanceledItems = countItems(canceledItemsInOrder);

        if (numOfCanceledItems > 3) {
          dispatch(ordersActions.setStoreCreatAmount('75.00'));

          return dispatch(
            addEdgeToDecisionTreePath(STORE_CREDIT_STATUS_TOKENS.FOUR_OR_MORE_ITEMS_CANCELED_OR_PREVIOUS_CANCELLATIONS)
          );
        } else if (numOfCanceledItems > 1 && numOfCanceledItems < 4) {
          dispatch(ordersActions.setStoreCreatAmount('50.00'));

          return dispatch(addEdgeToDecisionTreePath(STORE_CREDIT_STATUS_TOKENS.TWO_OR_THREE_ITEMS_CANCELED));
        } else if (numOfCanceledItems < 2) {
          return dispatch(addEdgeToDecisionTreePath(STORE_CREDIT_STATUS_TOKENS.ONE_ITEM_CANCELED));
        }
      } else {
        return dispatch(addEdgeToDecisionTreePath(STORE_CREDIT_STATUS_TOKENS.SELECTED_ORDER_NOT_CANCELED));
      }
    } catch (error) {
      captureException(error);

      return showErrorPopup(`There was a problem loading the order information`, 'Error');
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const processOrderShippingRefund = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));
      // clear previous data
      dispatch(ordersActions.setRefundShippingAmount(0));

      const state = getState();
      const selectedOrder = getSelectedOrder(state);
      const orderingSystem = getOrderingSystem(state);
      const orderId = getOrderId(selectedOrder, orderingSystem);

      if (!['shopify'].includes(orderingSystem)) {
        return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
      }

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      if (isOrderShippingRefunded(selectedOrder, orderingSystem)) {
        return dispatch(addEdgeToDecisionTreePath('already_refunded_shipping'));
      }

      if (!hasShippingCharges(selectedOrder, orderingSystem)) {
        return dispatch(addEdgeToDecisionTreePath('no_shipping_charges'));
      }

      if (orderingSystem === 'shopify') {
        const options = {
          shopName: selectedOrder.shopName,
          orderId,
        };

        const shippingRefundAmount = await dispatch(shopifyGetOrderShippingRefundAmount(options));
        dispatch(setRefundShippingAmount(shippingRefundAmount));
      }

      dispatch(addEdgeToDecisionTreePath('order_valid_for_refund'));
    } catch (e) {
      return showErrorPopup(`Failed processing order data`, 'Error');
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

// processes orders items selected in the cancel item flow
export const processOrderItemsCancelAndRefundFlow = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));

      const state = getState();
      const selectedOrder = getSelectedOrder(state);
      const orderingSystem = getOrderingSystem(state);
      const itemStatus = getStatusOfItemsOfSelectedOrderByItemId(state);
      const orderId = getOrderId(selectedOrder, orderingSystem);
      const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const allowFulfilledRefunds = decisionNode.allowFulfilledRefunds;
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;

      // 1. clears previous data
      dispatch(ordersActions.clearSnippetText());
      dispatch(ordersActions.clearCancelRefundDiscountTreeItems());
      dispatch(ordersActions.setRefundAmount(0));

      // 2. Process order-level data - add path if needed
      if ('shopify' !== orderingSystem) {
        return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
      }

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      // only triggered by fully refunded orders - partial refunds pass
      if (isOrderRefunded(selectedOrder, orderingSystem)) {
        return dispatch(
          addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.ORDER_IS_ALREADY_REFUNDED)
        );
      }

      // only triggered by fully canceled orders - orders w/ canceled items pass
      if (isOrderCanceled(selectedOrder, orderingSystem)) {
        return dispatch(
          addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.ORDER_IS_ALREADY_CANCELED)
        );
      }

      // 3. Get order cancel info
      const payload = {
        simplrAccountId,
        orderId: String(orderId),
        shopType: orderingSystem,
        shopName: selectedOrder.shopName,
      };

      // NOTE: Fulfilled items are not included in 'refundableItems'
      // You CAN refund fulfilled items optionally in this tree
      const { refundableItems } = await commonOrdersPostQuery('/cancelInfo', {}, payload);

      if (!refundableItems.length && !allowFulfilledRefunds) {
        return dispatch(
          addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.INVALID_ENTIRELY_FULFILLED)
        );
      }

      // 4. Process each item to determine if they qualify for a cancellation and refund
      const invalidAlreadyRefundedSnippetData = [];
      const invalidFulfilledSnippetData = []; // only used if !allowFulfilledRefunds
      const validFulfilledSnippetData = []; // only used if allowFulfilledRefunds
      const validUnfulfilledSnippetData = [];

      selectedItems.forEach((item) => {
        const itemSnippetData = {};
        itemSnippetData.itemName = getItemName(item, orderingSystem);
        const itemIsFulfilled = itemStatus[getItemId(item, orderingSystem)] !== 'unfulfilled';

        if (itemIsFulfilled) {
          if (allowFulfilledRefunds) {
            const itemIsCompletelyRefunded = isItemCompletelyRefunded(selectedOrder, item, orderingSystem);
            if (itemIsCompletelyRefunded) {
              invalidAlreadyRefundedSnippetData.push(itemSnippetData);
            } else {
              dispatch(ordersActions.addRefundOnlyItem(item));
              validFulfilledSnippetData.push(itemSnippetData);
            }
          } else {
            invalidFulfilledSnippetData.push(itemSnippetData);
          }
        } else {
          // item is either fulfilled, fully refunded or canceled
          const itemIsRefundable = refundableItems.some(
            (refundableItem) => parseInt(refundableItem.id, 10) === parseInt(getItemId(item, orderingSystem), 10)
          );

          if (itemIsRefundable) {
            // partial refunds still enter this block
            dispatch(ordersActions.addCancelAndRefundItem(item));
            validUnfulfilledSnippetData.push(itemSnippetData);
          } else {
            invalidAlreadyRefundedSnippetData.push(itemSnippetData);
          }
        }
      });

      // 5. Create and store snippets based on processed data
      if (invalidAlreadyRefundedSnippetData.length) {
        const refundedItemsSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidRefundedSnippet,
          invalidAlreadyRefundedSnippetData
        );
        refundedItemsSnippet && dispatch(addSnippetText(refundedItemsSnippet));
      }

      if (invalidFulfilledSnippetData.length) {
        const fulfilledItemsSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemInvalidFulfilledSnippet,
          invalidFulfilledSnippetData
        );
        fulfilledItemsSnippet && dispatch(addSnippetText(fulfilledItemsSnippet));
      }

      if (validFulfilledSnippetData.length) {
        const validItemsSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemValidFulfilledSnippet,
          validFulfilledSnippetData
        );
        validItemsSnippet && dispatch(addSnippetText(validItemsSnippet));
      }

      if (validUnfulfilledSnippetData.length) {
        const validItemsSnippet = replaceTemplatedTextForMultipleItems(
          decisionNode.itemValidUnfulfilledSnippet,
          validUnfulfilledSnippetData
        );
        validItemsSnippet && dispatch(addSnippetText(validItemsSnippet));
      }

      // 7. Check if there are items to cancel or refund
      const validItemsExist = allowFulfilledRefunds
        ? !!validUnfulfilledSnippetData.length || !!validFulfilledSnippetData.length
        : !!validUnfulfilledSnippetData.length;

      if (validItemsExist) {
        // 8. Get total refund
        const { total: totalRefund, currency } = await dispatch(ordersActions._getRefundForCancelRefundFlow(orderId));

        const totalRefundFormatted = priceFormat(totalRefund, currency);

        // 9. Include total refund in final macro
        const refundSummarySnippet = replaceTemplatedTextForMultipleItems(decisionNode.refundSummarySnippet, [
          { totalRefund, totalRefundFormatted },
        ]);
        refundSummarySnippet && dispatch(addSnippetText(refundSummarySnippet));

        const totalRefundInCents = convertDollarsToCents(totalRefund);

        // total refund amount is greater than allowed refund limit
        if (decisionNode.centsAmountLimit && totalRefundInCents >= parseInt(decisionNode.centsAmountLimit)) {
          return dispatch(
            addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.REFUND_AMOUNT_LIMIT_EXCEEDED)
          );
        }

        // 10. Push to confirmation page
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.VALID_ITEMS_EXIST));
      } else {
        // Hits this path if item was restocked but not fulfilled
        dispatch(addEdgeToDecisionTreePath(CANCEL_AND_REFUND_OR_DISCOUNT_DECISION_EDGES.NO_VALID_ITEMS_EXIST));
      }
    } catch (e) {
      return showErrorPopup(`Failed processing order data`, 'Error');
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

// processes partial refund for the selected items
export const processOrderItemsPartialRefund = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'searchPage'));

      const state = getState();
      const selectedOrder = getSelectedOrder(state);
      const orderingSystem = getOrderingSystem(state);
      const orderId = getOrderId(selectedOrder, orderingSystem);
      const userSelectedItems = getSelectedLineItems(state);
      const lineItemsOfSelectedOrder = getLineItemsOfSelectedOrder(state);
      const decisionNode = getDecisionNodeAtPath(state);
      const decisionNodeAmount = parseFloat(decisionNode.amount);
      const selectedItems = userSelectedItems.length ? userSelectedItems : lineItemsOfSelectedOrder;

      // 1. clears previous data
      dispatch(resetPartialRefundStore());

      // 2. Process order-level data - add path if needed
      if (decisionNodeAmount <= 0) {
        return showErrorPopup(`decisionNodeAmount is ${decisionNodeAmount}`, 'Error');
      }

      if ('shopify' !== orderingSystem) {
        return showErrorPopup(`${orderingSystem} is not supported`, 'Error');
      }

      if (!getOrderModificationsAreAllowed(state)) {
        return showErrorPopup(`${orderingSystem} order modifications are not allowed`, 'Error');
      }

      if (isOrderPartiallyOrFullyRefunded(selectedOrder, orderingSystem)) {
        return dispatch(addEdgeToDecisionTreePath(PARTIAL_REFUND_DECISION_EDGES.ITEMS_ALREADY_REFUNDED));
      }

      const refundItems = processForRefundCalculation(selectedItems, orderingSystem);

      const payload = {
        orderId: String(orderId),
        uniqueRequestUuid: uuid(),
        refundItems,
      };

      const calculations = await dispatch(fetchCalculateInfo(payload));
      const amountToRefundPossible = parseFloat(calculations.total);
      const centsAmountLimit = parseInt(decisionNode.centsAmountLimit);

      if (amountToRefundPossible <= 0) {
        return dispatch(addEdgeToDecisionTreePath(PARTIAL_REFUND_DECISION_EDGES.ITEMS_ALREADY_REFUNDED));
      }
      // This is the notice for the future developers making changes to this action
      // The amountToRefund is calculated based on the amount to refund left for the selected items.
      // This means that the first time we making a refund - that will be calculated from a 100% of the total amount paid for the selected items
      // But for the next refund - this amount will be calculated from a total amount paid reduced by the amount of the previous refund.
      // To avoid any issues connected with wrong refund calculation - we have limited the number of possible refunds to 1.
      // So if there was any previous refund of any item in the order it would trigger PARTIAL_REFUND_DECISION_EDGES.ITEMS_ALREADY_REFUNDED path
      // Make sure you are understand the above before make any changes to this logic, especially when it comes to the multiple refunds enabling
      const amountToRefund =
        decisionNode.typeOfRefund.name === REFUND_AMOUNT_TYPE.PERCENT
          ? (decisionNodeAmount * amountToRefundPossible) / 100
          : decisionNodeAmount;

      if (amountToRefund > amountToRefundPossible) {
        return dispatch(addEdgeToDecisionTreePath(PARTIAL_REFUND_DECISION_EDGES.REFUND_LAGER_THAN_TOTAL_PAID));
      }

      if (convertDollarsToCents(amountToRefund) > centsAmountLimit) {
        return dispatch(addEdgeToDecisionTreePath(PARTIAL_REFUND_DECISION_EDGES.REFUND_AMOUNT_LIMIT_EXCEEDED));
      }
      dispatch(setPartialRefundAmount(amountToRefund));

      return dispatch(addEdgeToDecisionTreePath(PARTIAL_REFUND_DECISION_EDGES.ALL_VALID_ITEMS));
    } catch (e) {
      captureException(e);

      return showErrorPopup(`Failed processing order data`, 'Error');
    } finally {
      dispatch(setLoading(false, 'searchPage'));
    }
  };
};

export const processRefundOrder = () => async (dispatch, getState) => {
  const state = getState();

  const returnedItems = getReturnedItems(state);
  const selectedOrder = getSelectedOrder(state);
  const orderingSystem = getOrderingSystem(state);
  const orderId = getMappedValueFromOrderOrItem(selectedOrder, 'orderId', orderingSystem);
  const refundAmount = getRefundAmount(state);

  const refundItems = returnedItems.map((item) => {
    return {
      id: item['item_id'],
      quantity: item['qty_shipped'],
    };
  });

  const userId = AuthService.current().simplrExpert.id;

  const payload = {
    userId,
    refundAmount,
    orderId,
    refundItems,
    timeOut: 70000,
    comment: 'This refund was for an item that was already returned',
    addNotes: true,
  };

  dispatch(loadLongTransaction());
  await dispatch(cancelRefund(payload));
  trackDecisionTreeItemRefunded();

  // confirmation view relies on qty_refunded for returnedItems
  const refundedData = returnedItems.map((item) => {
    return Object.assign({}, item, {
      qty_refunded: '1',
    });
  });
  dispatch(setReturnedItems(refundedData));
};
